import React, { useState, useEffect } from 'react'
import { Button, Form, Modal, Table, Spinner, Alert } from 'react-bootstrap'
import { expanceListingAPI } from '../../../API/litigationApis'
import { showToast } from "../../../utils/handleToast"

const AddExpenseModal = ({
    showExpenseModal,
    setShowExpenseModal,
    expenseType,
    setExpenseType,
    expenseAmount,
    setExpenseAmount,
    submitExpense,
    expenseLoading,
    selectedCase
}) => {
    const [expenses, setExpenses] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    // Fetch expenses when modal opens
    useEffect(() => {
        if (showExpenseModal && selectedCase?.case_id) {
            fetchExpenses()
        }
    }, [showExpenseModal, selectedCase])

    const fetchExpenses = async () => {
        try {
            setLoading(true)
            setError(null)

            const params = { case_id: selectedCase.case_id }
            const response = await expanceListingAPI(params)

            if (response?.status && response?.data) {
                setExpenses(response.data.results || [])
            } else {
                throw new Error(response?.message || "Failed to fetch expenses")
            }
        } catch (error) {
            console.error("Error fetching expenses:", error)
            setError(error.message || "Failed to fetch expenses")
            showToast({
                variant: "error",
                message: error.message || "Failed to fetch expenses"
            })
        } finally {
            setLoading(false)
        }
    }

    const handleClose = () => {
        setShowExpenseModal(false)
        setExpenses([])
        setError(null)
    }

    // Format currency helper
    const formatCurrency = (amount) => {
        if (!amount) return '₹0'
        return `₹${parseFloat(amount).toLocaleString('en-IN')}`
    }

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return '--'
        try {
            const date = new Date(dateString)
            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            })
        } catch {
            return '--'
        }
    }

    // Calculate total expenses
    const totalExpenses = expenses.reduce(
        (sum, expense) => sum + parseFloat(expense.amount || 0),
        0
    )

    return (
        <Modal show={showExpenseModal} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    Out-of-pocket Expenses
                    {selectedCase && (
                        <div className="text-muted fs-6 mt-1">
                            {selectedCase.customer_name} ({selectedCase.lan_number})
                        </div>
                    )}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>

                {/* ------------------ View Expenses Section ------------------ */}
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2 text-muted">Loading expenses...</p>
                    </div>
                ) : error ? (
                    <Alert variant="danger">
                        <Alert.Heading>Error</Alert.Heading>
                        <p>{error}</p>
                        <Button variant="outline-danger" size="sm" onClick={fetchExpenses}>
                            Try Again
                        </Button>
                    </Alert>
                ) : expenses.length === 0 ? (
                    <div className="text-center py-4">
                        <p className="text-muted">No expenses found for this case.</p>
                    </div>
                ) : (
                    <>
                        <div className="mb-3">
                            <div className="row">
                                <div className="col-md-6">
                                    <small className="text-muted">Total Expenses:</small>
                                    <div className="fw-bold fs-5">
                                        {formatCurrency(totalExpenses)}
                                    </div>
                                </div>
                                <div className="col-md-6 text-end">
                                    <small className="text-muted">
                                        {expenses.length} expense(s) recorded
                                    </small>
                                </div>
                            </div>
                        </div>

                        <div className="table-responsive">
                            <Table striped hover>
                                <thead className="table-gray">
                                    <tr>
                                        <th>S.No.</th>
                                        <th>Expense Type</th>
                                        <th>Amount</th>
                                        <th>Date Added</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {expenses.map((expense, index) => (
                                        <tr key={expense.id}>
                                            <td>{index + 1}</td>
                                            <td className="text-capitalize">{expense.expense_type}</td>
                                            <td className="fw-bold text-success">
                                                {formatCurrency(expense.amount)}
                                            </td>
                                            <td>
                                                <small className="text-muted">
                                                    {formatDate(expense.created_at)}
                                                </small>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </>
                )}
                
                {/* ------------------ Add Expense Form ------------------ */}
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Expense Type *</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter expense type (e.g., travelling)"
                            value={expenseType}
                            onChange={(e) => setExpenseType(e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Amount *</Form.Label>
                        <Form.Control
                            type="number"
                            placeholder="Enter amount"
                            value={expenseAmount}
                            onChange={(e) => setExpenseAmount(e.target.value)}
                            min="0"
                            step="0.01"
                        />
                    </Form.Group>

                    <div className="text-end mb-4">
                        <Button
                            variant="primary"
                            onClick={submitExpense}
                            disabled={expenseLoading}
                        >
                            {expenseLoading ? 'Adding..' : 'Add Expense'}
                        </Button>
                    </div>
                </Form>

             
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
                {expenses.length > 0 && (
                    <Button variant="outline-primary" onClick={fetchExpenses}>
                        Refresh
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    )
}

export default AddExpenseModal
